import type { EntityATI } from '@atlassian/search-client/common/types';

import type { AllFilterStates } from '../../../common/constants/filters/types';
import { type TypeFilterValueKey } from '../../../common/constants/filters/universal-type';
import { type ProductKeys } from '../../../common/constants/product-keys';
import { type ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';

import { getEntitiesForQuery as getUniversalEntitiesForQuery } from './filters/universal-type/utils';

/**
 * Returns type entities for the "universal" (cross-product) search scope,
 * merging all available product type entities and applying third-party config overrides.
 *
 * Pure function — safe for SSR and non-React contexts.
 */
export const getUniversalTypeEntities = (
	productsToSearch: ProductKeys[],
	typeFilter: AllFilterStates | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	availableProducts: ProductKeys[],
	selectedProducts: ProductKeys[] | undefined,
): {
	selectedEntities: TypeFilterValueKey[];
	entitiesForQuery: EntityATI[];
	entitiesForResultsCountQuery: EntityATI[];
	selectedTypes: TypeFilterValueKey[];
} => {
	// This early return is to appease Typescript. The typeFilter is always defined and is always single select.
	if (!typeFilter || !(typeFilter.type === 'single-select')) {
		return {
			selectedEntities: [],
			entitiesForQuery: [],
			entitiesForResultsCountQuery: [],
			selectedTypes: [],
		};
	}
	const defaultOptionValue = typeFilter.options?.find((option) => option.isDefault)?.value;

	const selectedTypes = (typeFilter.value as TypeFilterValueKey[])
		.filter((value) => value !== defaultOptionValue)
		.filter(Boolean);

	const { entitiesForQuery, entitiesForResultsCountQuery } = getUniversalEntitiesForQuery(
		productsToSearch,
		selectedTypes,
		thirdPartyConfigs,
		availableProducts,
		selectedProducts,
	);

	return {
		selectedEntities: selectedTypes,
		entitiesForQuery: entitiesForQuery,
		entitiesForResultsCountQuery,
		selectedTypes,
	};
};
